package com.oscarherreros.appcalculo.web

import com.oscarherreros.appcalculo.game.DailyProgress
import kotlinx.browser.localStorage
import org.w3c.dom.Storage

/** Dónde se guardan los datos: el navegador o, en los tests, un mapa en memoria. */
interface KeyValueStore {
    fun get(key: String): String?

    /** Guarda [value] en [key], o la borra si es null. */
    fun set(key: String, value: String?)
}

/**
 * El almacenamiento local del navegador. Si no se puede usar (navegación privada
 * estricta, datos de sitios bloqueados), se juega igual, pero sin guardar nada.
 */
class BrowserStore : KeyValueStore {
    private val storage: Storage? = try {
        localStorage
    } catch (e: Throwable) {
        null
    }

    override fun get(key: String): String? = try {
        storage?.getItem(key)
    } catch (e: Throwable) {
        null
    }

    override fun set(key: String, value: String?) {
        try {
            if (value == null) storage?.removeItem(key) else storage?.setItem(key, value)
        } catch (e: Throwable) {
            // Sin espacio o sin permiso: se pierde el guardado, no la partida.
        }
    }
}

private const val PREFIX = "kalkulia."
private const val LAST_DAY = PREFIX + "daily_last_day"
private const val STREAK = PREFIX + "daily_streak"
private const val STARS = PREFIX + "daily_stars"
private const val GAME = PREFIX + "daily_game"
private const val DAY_SEPARATOR = "/"

/** La racha del reto diario y la partida a medias, guardadas como en la app. */
class DailyStore(private val store: KeyValueStore) {
    fun loadProgress(): DailyProgress {
        val day = store.get(LAST_DAY)?.toLongOrNull() ?: return DailyProgress()
        return DailyProgress(day, store.get(STREAK)?.toIntOrNull() ?: 0, store.get(STARS)?.toIntOrNull() ?: 0)
    }

    fun saveProgress(progress: DailyProgress) {
        store.set(LAST_DAY, progress.lastCompletedDay?.toString())
        store.set(STREAK, progress.streak.toString())
        store.set(STARS, progress.stars.toString())
    }

    /** La partida a medias del reto de [day], como la dejó [saveGame]; null si no hay o es de otro día. */
    fun loadGame(day: Long): String? {
        val saved = store.get(GAME) ?: return null
        return saved.substringAfter(DAY_SEPARATOR).takeIf { saved.substringBefore(DAY_SEPARATOR) == day.toString() }
    }

    /** Guarda la partida del reto de [day] codificada con `encode`, o la borra si es null. */
    fun saveGame(day: Long, encoded: String?) {
        store.set(GAME, encoded?.let { day.toString() + DAY_SEPARATOR + it })
    }
}
